import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Globe, Layers, Loader2, Search, Server as ServerIcon, Tag, Monitor } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { innerSurface } from '@/components/surface/tones';
import { Pill } from '@/components/surface/ui';
import { Select } from '@/components/ui/select';
import { AUTOMATION_CATALOG_KEY, automationApi } from '@/lib/automation-api';
import { getErrorMessage } from '@/lib/errors';
import { serversApi } from '@/lib/servers-api';
import { cn } from '@/lib/utils';
import { isCompleteSelection } from '@/modules/automation/parameters';
import { Chip, Segmented } from '@/modules/automation/ui';
import type { RunKind, TargetMode, TargetPreview, TargetSelection } from '@/types/api';

const STATUS_TONE = {
  online: 'success',
  unverified: 'muted',
  offline: 'danger',
  unreachable: 'danger',
  unsupported_os: 'warning',
} as const;

/**
 * Which servers a run reaches: some chosen by name, a group, a tag, operating systems, or all of them.
 *
 * ## The preview is the point
 *
 * Whatever is chosen, the servers it resolves to are listed underneath with what the platform already
 * expects to go wrong on each — never connected, a playbook written for another distribution, an
 * account that cannot become root — so "every server" shows its three problem machines before Run is
 * pressed rather than after. The preview changes nothing and contacts no server; the preflight checks
 * every machine again when the run starts.
 */
export function TargetPicker({
  value,
  onChange,
  kind,
  subject,
  error,
  onPreview,
}: {
  value: TargetSelection;
  onChange: (selection: TargetSelection) => void;
  kind: RunKind;
  subject: string;
  error?: string | undefined;
  onPreview?: (preview: TargetPreview | null) => void;
}) {
  const [search, setSearch] = useState('');

  const servers = useQuery({ queryKey: ['servers', 'all'], queryFn: () => serversApi.all(), staleTime: 30_000 });
  const inventory = useQuery({ queryKey: ['server-inventory'], queryFn: () => serversApi.inventory(), staleTime: 30_000 });
  const catalog = useQuery({ queryKey: AUTOMATION_CATALOG_KEY, queryFn: () => automationApi.catalog(), staleTime: 300_000 });

  const complete = isCompleteSelection(value);
  const selectionKey = JSON.stringify([value.mode, [...value.values].sort()]);

  const preview = useQuery({
    queryKey: ['automation', 'preview', kind, subject, selectionKey],
    queryFn: () => automationApi.previewTargets(value, kind, subject),
    enabled: complete,
    staleTime: 15_000,
    placeholderData: (previous) => previous,
  });

  const previewData = complete ? preview.data ?? null : null;

  useEffect(() => {
    onPreview?.(previewData);
  }, [previewData, onPreview]);

  const fleet = useMemo(() => servers.data ?? [], [servers.data]);
  const families = useMemo(() => {
    const present = new Map<string, number>();
    for (const server of fleet) {
      if (server.os_family !== null) {
        present.set(server.os_family, (present.get(server.os_family) ?? 0) + 1);
      }
    }
    return present;
  }, [fleet]);

  const setMode = (mode: TargetMode) => {
    if (mode === value.mode) return;
    onChange({ mode, values: [] });
  };

  const toggleValue = (item: string) =>
    onChange({
      mode: value.mode,
      values: value.values.includes(item) ? value.values.filter((v) => v !== item) : [...value.values, item],
    });

  const needle = search.trim().toLowerCase();
  const shownServers = needle === ''
    ? fleet
    : fleet.filter((server) =>
        [server.name, server.ssh_host, server.os_label ?? '', ...server.tags].some((text) => text.toLowerCase().includes(needle)),
      );

  return (
    <div className="space-y-4">
      <Segmented<TargetMode>
        ariaLabel="Choose servers by"
        value={value.mode}
        onChange={setMode}
        options={[
          { value: 'servers', label: 'Servers', icon: ServerIcon },
          { value: 'group', label: 'Group', icon: Layers },
          { value: 'tag', label: 'Tag', icon: Tag },
          { value: 'os', label: 'Operating system', icon: Monitor },
          { value: 'all', label: 'All servers', icon: Globe },
        ]}
      />

      {servers.isError && (
        <p className="text-sm text-danger">Your servers could not be loaded: {getErrorMessage(servers.error)}</p>
      )}

      {value.mode === 'servers' && (
        <div className={cn(innerSurface, 'overflow-hidden')}>
          <div className="flex items-center gap-2 border-b border-slate-900/[0.07] px-3 py-2 dark:border-white/[0.07]">
            <Search className="size-4 text-muted-foreground" aria-hidden />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              aria-label="Filter servers by name, address, operating system or tag"
              className="h-8 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
            <button
              type="button"
              className="text-xs font-medium text-primary hover:underline disabled:opacity-50"
              onClick={() => onChange({ mode: 'servers', values: Array.from(new Set([...value.values, ...shownServers.map((s) => s.id)])) })}
              disabled={shownServers.length === 0}
            >
              Select {needle === '' ? 'all' : 'shown'}
            </button>
            {value.values.length > 0 && (
              <button type="button" className="text-xs text-muted-foreground hover:text-foreground" onClick={() => onChange({ mode: 'servers', values: [] })}>
                Clear
              </button>
            )}
          </div>

          <ul className="max-h-72 divide-y divide-slate-900/[0.06] overflow-y-auto dark:divide-white/[0.05]" aria-label="Servers">
            {servers.isLoading && (
              <li className="flex items-center gap-2 px-3 py-4 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" aria-hidden /> Loading servers…
              </li>
            )}
            {servers.isSuccess && fleet.length === 0 && (
              <li className="px-3 py-4 text-sm text-muted-foreground">
                You have no servers yet. <Link to="/servers" className="text-primary hover:underline">Add one</Link> to run anything.
              </li>
            )}
            {servers.isSuccess && fleet.length > 0 && shownServers.length === 0 && (
              <li className="px-3 py-4 text-sm text-muted-foreground">No server matches “{search}”.</li>
            )}
            {shownServers.map((server) => {
              const checked = value.values.includes(server.id);

              return (
                <li key={server.id}>
                  <label className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-foreground/[0.03]">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleValue(server.id)}
                      className="size-4 rounded border-input accent-[hsl(var(--primary))]"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{server.name}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {server.endpoint}
                        {server.os_label !== null && ` · ${server.os_label}`}
                      </span>
                    </span>
                    <Pill tone={STATUS_TONE[server.status]}>{server.status_label}</Pill>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {value.mode === 'group' && (
        <Select
          aria-label="Group"
          value={value.values[0] ?? ''}
          onChange={(event) => onChange({ mode: 'group', values: event.target.value === '' ? [] : [event.target.value] })}
        >
          <option value="">Choose a group…</option>
          {(inventory.data?.groups ?? []).map((group) => (
            <option key={group.id} value={group.id}>
              {group.name} ({group.server_count} server{group.server_count === 1 ? '' : 's'})
            </option>
          ))}
        </Select>
      )}

      {value.mode === 'tag' && (
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Tags">
          {(inventory.data?.tags ?? []).length === 0 && (
            <p className="text-sm text-muted-foreground">None of your servers has a tag yet. Tags are set on the Servers page.</p>
          )}
          {(inventory.data?.tags ?? []).map((tag) => (
            <Chip key={tag.name} selected={value.values.includes(tag.name)} onClick={() => toggleValue(tag.name)}>
              {tag.name} <span className="ml-1 text-muted-foreground">{tag.count}</span>
            </Chip>
          ))}
        </div>
      )}

      {value.mode === 'os' && (
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Operating systems">
          {(catalog.data?.operating_systems ?? []).map((system) => {
            const count = families.get(system.value) ?? 0;

            return (
              <Chip key={system.value} selected={value.values.includes(system.value)} onClick={() => toggleValue(system.value)} disabled={count === 0}>
                {system.label} <span className="ml-1 text-muted-foreground">{count}</span>
              </Chip>
            );
          })}
        </div>
      )}

      {value.mode === 'all' && (
        <p className="text-sm text-muted-foreground">
          Every server on your account{inventory.data ? `, ${inventory.data.counts.total} right now` : ''}. Servers added before the run
          starts are included; the preflight checks each one first.
        </p>
      )}

      {error !== undefined && error !== '' && (
        <p className="text-sm text-danger" role="alert">
          {error}
        </p>
      )}

      <TargetPreviewList complete={complete} loading={preview.isFetching && previewData === null} preview={previewData} failure={preview.isError ? getErrorMessage(preview.error) : null} />
    </div>
  );
}

function TargetPreviewList({
  complete,
  loading,
  preview,
  failure,
}: {
  complete: boolean;
  loading: boolean;
  preview: TargetPreview | null;
  failure: string | null;
}) {
  const [expanded, setExpanded] = useState(false);

  if (!complete) {
    return null;
  }

  if (loading) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" aria-hidden /> Checking which servers this reaches…
      </p>
    );
  }

  if (failure !== null) {
    return <p className="text-sm text-danger">{failure}</p>;
  }

  if (preview === null) {
    return null;
  }

  if (preview.problems.length > 0) {
    return (
      <div className="rounded-lg bg-danger/[0.06] px-3 py-2.5 text-sm dark:bg-danger/[0.08]" role="alert">
        {preview.problems.map((problem) => (
          <p key={problem}>{problem}</p>
        ))}
      </div>
    );
  }

  const withWarnings = preview.servers.filter((server) => server.warnings.length > 0);
  const shown = expanded ? preview.servers : preview.servers.slice(0, 6);

  return (
    <div className={cn(innerSurface, 'p-3')}>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-[13px] font-medium">
          Runs on {preview.servers.length} server{preview.servers.length === 1 ? '' : 's'}
        </p>
        {withWarnings.length > 0 ? (
          <Pill tone="warning" icon={AlertTriangle}>
            {withWarnings.length} with warnings
          </Pill>
        ) : (
          <Pill tone="success">No known problems</Pill>
        )}
      </div>

      <ul className="space-y-1.5">
        {shown.map((server) => (
          <li key={server.id} className="text-[13px]">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="font-medium">{server.name}</span>
              <span className="text-xs text-muted-foreground">
                {server.os_name ?? 'System not detected yet'}
                {server.group_name !== null && ` · ${server.group_name}`}
              </span>
            </div>
            {server.warnings.map((warning) => (
              <p key={warning} className="flex items-start gap-1.5 text-xs text-warning">
                <AlertTriangle className="mt-0.5 size-3 shrink-0" aria-hidden />
                {warning}
              </p>
            ))}
          </li>
        ))}
      </ul>

      {preview.servers.length > 6 && (
        <button type="button" onClick={() => setExpanded((open) => !open)} className="mt-2 text-xs font-medium text-primary hover:underline">
          {expanded ? 'Show fewer' : `Show all ${preview.servers.length}`}
        </button>
      )}
    </div>
  );
}
